import Input from 'components/final-form/Input';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import {
  EntriesConsumerRenderProps,
  ExtendedBaseEntryDto,
  withEntriesContext
} from 'components/segments/entires/EntriesContext';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, Modal } from 'semantic-ui-react';
import { ProcedureType } from 'ts-types/api.enums';
import { AdditionalEffort } from 'ts-types/api.types';
import { numberFormatter } from 'utils/fomattingUtils';
import {
  composeValidators,
  mustBeNumber,
  mustBePositiveNumber,
  mustNotBeZero,
  validateOnlyIfPresent
} from 'utils/validatorUtils';

enum AdditionalEffortType {
  LAB = "18",
  MATERIAL = "34"
}

interface Procedure {
  quantity: number,
  value: number,
  rate: number,
  lab: number,
  material: number
}

interface Props extends MessageSourceProps, EntriesConsumerRenderProps {
  modalOpen: boolean,
  onCloseModal: () => void,
  procedure: ExtendedBaseEntryDto
}

interface State {
  procedure: Partial<Procedure>
}

class ProcedureModal extends Component<Props, State> {

  state = {
    procedure: {}
  };

  componentDidUpdate({modalOpen: prevModalOpen}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {modalOpen, procedure} = this.props;

    if (!prevModalOpen && modalOpen) {

      const lab = procedure.additionalEfforts.find(additionalEffort => additionalEffort.oldType == AdditionalEffortType.LAB);
      const material = procedure.additionalEfforts.find(additionalEffort => additionalEffort.oldType == AdditionalEffortType.MATERIAL);

      let editProcedure: Partial<Procedure> = {
        quantity: procedure.quantity,
        value: procedure.actualValue != procedure.value ? procedure.value : undefined,
        rate: procedure.percentage,
        lab: lab ? lab.value : undefined,
        material: material ? material.value : undefined
      };

      this.setState({
        procedure: editProcedure
      })
    }
  }

  handleSubmit = async (values: Partial<Procedure>) => {

    const {onCloseModal, saveEntry, procedure} = this.props;

    let value = procedure.actualValue;
    let calculatedValue = procedure.actualValue * procedure.taxPoint;

    if (values.value) {
      value = +values.value;
    }

    if (values.rate) {
      calculatedValue = +values.rate > 0 ? (value * procedure.taxPoint) * (+values.rate / 100) : 0;
    } else {
      calculatedValue = value * procedure.taxPoint;
    }

    const additionalEfforts: Array<AdditionalEffort> = [];

    const prevLab = procedure.additionalEfforts.find(additionalEffort => additionalEffort.oldType == AdditionalEffortType.LAB);
    const prevMaterial = procedure.additionalEfforts.find(additionalEffort => additionalEffort.oldType == AdditionalEffortType.MATERIAL);

    const defaultValues = {
      id: -1,
      treaterId: -1,
      // TODO OC: Why it is called parent ID, it makes so complicated. AAAAAAAAAAAAAAAAA
      parentId: procedure.procedureId,
      quantity: !!values.quantity ? +values.quantity : 1,
      type: ProcedureType.CHF
    };

    if (values.lab) {

      let labEffort: AdditionalEffort;

      if (prevLab) {
        labEffort = {
          ...prevLab,
          quantity: !!values.quantity ? +values.quantity : 1,
          value: values.lab
        }
      } else {
        labEffort = {
          ...defaultValues,
          description: "msgSpecialLabor",
          oldType: AdditionalEffortType.LAB,
          value: values.lab
        }
      }

      additionalEfforts.push(labEffort);
    }

    if (values.material) {

      let materialEffort: AdditionalEffort;

      if (prevMaterial) {
        materialEffort = {
          ...prevMaterial,
          quantity: !!values.quantity ? +values.quantity : 1,
          value: values.material
        }
      } else {
        materialEffort = {
          ...defaultValues,
          description: "msgSpecialMaterial",
          oldType: AdditionalEffortType.MATERIAL,
          value: values.material
        }
      }

      additionalEfforts.push(materialEffort);
    }

    const modifiedProcedure = {
      ...procedure,
      quantity: values.quantity ? +values.quantity : 1,
      value: value,
      percentage: values.rate ? +values.rate : undefined,
      calculatedValue: calculatedValue,
      additionalEfforts: additionalEfforts
    };

    await saveEntry(modifiedProcedure);
    onCloseModal();
  };

  render(): React.ReactNode {

    const {modalOpen, onCloseModal, getMessage} = this.props;
    const {procedure} = this.state;

    return (
        <Modal
            open={modalOpen}
            onClose={onCloseModal}
            style={{width: "600px"}}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("procedureModal.header")}</Modal.Header>
          <FormModalContentContainer>
            <FinalForm
                onSubmit={this.handleSubmit}
                subscription={{pristine: true, submitting: true}}
                render={this.renderForm}
                initialValues={procedure}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCloseModal} = this.props;

    const validator = validateOnlyIfPresent(composeValidators(mustBeNumber, mustBePositiveNumber, mustNotBeZero));

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormGrid>
                {this.renderQuantityAndValueSegment(validator)}
                {this.renderAdditionalEffortSegment(validator)}
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button type="submit" primary compact disabled={submitting}>{getMessage("button.save")}</Button>
              <Button type="button" compact disabled={submitting}
                      onClick={onCloseModal}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    )
  };

  renderQuantityAndValueSegment = (validator: any): React.ReactNode => {

    const {getMessage, procedure} = this.props;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("quantityAndValue")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}>
              {`${getMessage("quantity")}:`}
            </Grid.Column>
            <Grid.Column width={12}>
              <Field
                  name="quantity"
                  component={Input}
                  validate={validator}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          {
              procedure.variableAmount
              && <Grid.Row>
                <Grid.Column width={4}>
                  {`${getMessage("value")}:`}
                </Grid.Column>
                <Grid.Column width={12}>
                  <Field
                      name="value"
                      component={Input}
                      validate={validator}
                      fluid
                  />
                </Grid.Column>
              </Grid.Row>
          }
          <Grid.Row>
            <Grid.Column width={4}>
              {`${getMessage("originalValue")}:`}
            </Grid.Column>
            <Grid.Column width={12}>
              {!procedure.variableAmount ?
                  <>{`${numberFormatter.format(procedure.actualValue)} ${procedure.type}`}</>
                  : <>{`${numberFormatter.format(procedure.amountFrom)} - ${numberFormatter.format(procedure.amountTo)} ${procedure.type}`}</>
              }
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}>
              {`${getMessage("rate")}:`}
            </Grid.Column>
            <Grid.Column width={12}>
              <Field
                  name="rate"
                  component={Input}
                  validate={validateOnlyIfPresent(composeValidators(mustBeNumber, mustBePositiveNumber))}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  };

  renderAdditionalEffortSegment = (validator: any): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("procedureModal.additionalEffort")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}>
              {`${getMessage("procedure.lab")}:`}
            </Grid.Column>
            <Grid.Column width={12}>
              <Field
                  name="lab"
                  component={Input}
                  validate={validator}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={4}>
              {`${getMessage("procedure.material")}:`}
            </Grid.Column>
            <Grid.Column width={12}>
              <Field
                  name="material"
                  component={Input}
                  validate={validator}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    )
  }
}

export default withMessages(withEntriesContext(ProcedureModal));
